<template>
  <div v-if="showDetails" class="details-section">
    <div
        v-for="(detail, index) in meetingDetails"
        :key="index"
        class="meeting-details user-file"
        :class="{ 'page-break': index > 0 }"
    >
      <!-- En-tête avec photo et nom -->
      <div class="persona-header">
        <a :href="detail.details['Fiche candidat']?.url || ''" target="_blank" ><img
            v-if="detail.details['Fiche candidat']?.Photo"
            :src="detail.details['Fiche candidat'].Photo"
            @error="event => event.target.src = userProfileCircle"
            alt="Photo du candidat"
            class="persona-icon"
        />
        </a>
        <a :href="detail.details['Fiche candidat']?.url || ''" target="_blank" >
          <h3 class="persona-title">
            {{ detail.details['Fiche candidat']?.Prénom || '' }}
            {{ detail.details['Fiche candidat']?.Nom || '' }}

          </h3>
        </a>
        <h3 class="persona-subtitle" style="margin-left: 25px">{{ formatDate(detail.meeting.startAt) }} de {{ formatTime(detail.meeting.startAt) }} à {{ formatTime(detail.meeting.endAt) }}</h3>
      </div>

      <!-- Boucle sur les sections du détail -->
      <div v-for="(section, sectionName) in detail.details" :key="sectionName" :class="sectionName" class="section">
        <h4 class="section-title">{{ sectionName }}</h4>

        <div class="section-content ml-2">
          <!-- Section Documents avec icônes et preview intégrée -->
          <div v-if="sectionName === 'Documents'" class="documents-section">
            <div class="documents-grid">
              <div
                  v-for="(url, docType) in section"
                  :key="docType"
                  class="document-item"
              >
                <div
                    class="document-icon-wrapper"
                    @mouseenter="showPreview(index, docType, url)"
                >
                  <a :href="url" target="_blank" class="document-link">
                    <div class="document-icon">
                      <i class="document-icon-file"></i>
                      <span class="document-type">{{ getDocumentTypeLabel(docType) }}</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- Aperçu en pleine largeur -->
            <div
                v-if="getPreview(index).visible"
                class="document-preview-fullwidth"
                :class="{ 'show': getPreview(index).visible }"
            >
              <div class="preview-header">
                <span class="preview-title">Aperçu - {{ getDocumentTypeLabel(getPreview(index).type) }}</span>
                <button class="preview-close" @click="hidePreview(index)">×</button>
              </div>

              <div class="preview-content">
                <!-- PDF -->
                <iframe
                    v-if="getPreview(index).isPdf && !getPreview(index).error"
                    :src="getPreview(index).url"
                    class="preview-iframe"
                    frameborder="0"
                    @load="onPdfLoad(index)"
                ></iframe>

                <!-- Image -->
                <img
                    v-else-if="getPreview(index).isImage && !getPreview(index).error"
                    :src="getPreview(index).url"
                    :alt="getPreview(index).type"
                    class="preview-image"
                    @load="onImageLoad(index)"
                    @error="onPreviewError(index)"
                />

                <!-- État de chargement -->
                <div v-if="getPreview(index).isLoading" class="preview-loading">
                  <div class="loading-spinner"></div>
                  <p>Chargement de l'aperçu...</p>
                </div>

                <!-- Erreur -->
                <div v-else-if="getPreview(index).error" class="preview-error">
                  <i class="file-icon"></i>
                  <p>Impossible de charger l'aperçu</p>
                </div>

                <!-- Type non supporté -->
                <div v-else-if="!getPreview(index).isPdf && !getPreview(index).isImage" class="unsupported-preview">
                  <i class="file-icon"></i>
                  <p>Aperçu non disponible pour ce type de fichier</p>
                </div>
              </div>

              <!-- Bouton de téléchargement systématique -->
              <div class="preview-actions">
                <a :href="getPreview(index).url" target="_blank" class="download-button">
                  <i class="download-icon"></i>
                  Télécharger le document
                </a>
              </div>
            </div>
          </div>

          <!-- Sections normales -->
          <template v-else>
            <template v-for="(value, key) in section" :key="key">
              <p v-if="key !== 'Photo' && key !== 'url'" class="section-info">
                <strong>{{ key }} : </strong>
                <span v-html="value"></span>
              </p>
            </template>
          </template>

          <!-- Affichage des heures de formation pour la section "Mise à niveau" -->

        </div>
        <hr/>
      </div>

      <!-- Informations de meeting et planning -->
      <div class="section-content mt-3" style="display: flex; justify-content: flex-end;">
        <p><strong>Entretien réalisé par :</strong> {{ detail.planning.interviewManager.firstname }} {{ detail.planning.interviewManager.lastname }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useDateFormatter} from '@/composables/useDateFormatter'
import { ref, reactive } from 'vue'
import userProfileCircle from '@/assets/img/user-profile-circle.png'

defineProps({
  showDetails: {
    type: Boolean,
    required: true
  },
  meetingDetails: {
    type: Array,
    required: true,
    default: () => []
  }
})

const {formatDate, formatTime} = useDateFormatter()

// Gestion de la prévisualisation par meeting (utilise l'index comme identifiant)
const previews = reactive({})

const getPreview = (meetingIndex) => {
  if (!previews[meetingIndex]) {
    previews[meetingIndex] = reactive({
      visible: false,
      url: '',
      type: '',
      isLoading: false,
      isPdf: false,
      isImage: false,
      error: false
    })
  }
  return previews[meetingIndex]
}

const showPreview = (meetingIndex, docType, url) => {
  const preview = getPreview(meetingIndex)

  // Ne rien faire si l'aperçu est déjà visible pour ce document
  if (preview.visible && preview.type === docType) {
    return
  }

  preview.url = url
  preview.type = docType
  preview.isLoading = true
  preview.error = false

  // Déterminer le type de fichier
  const extension = url.split('.').pop().toLowerCase().split('?')[0]
  preview.isPdf = extension === 'pdf'
  preview.isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)

  // Afficher l'aperçu
  preview.visible = true

  // Timeout de sécurité si les événements load/error ne se déclenchent pas
  setTimeout(() => {
    if (preview.isLoading) {
      preview.isLoading = false
      preview.error = true
    }
  }, 5000) // 5 secondes timeout
}

const hidePreview = (meetingIndex) => {
  const preview = getPreview(meetingIndex)
  preview.visible = false
  preview.isLoading = false
  preview.error = false
}

const onImageLoad = (meetingIndex) => {
  const preview = getPreview(meetingIndex)
  preview.isLoading = false
  preview.error = false
}

const onPdfLoad = (meetingIndex) => {
  const preview = getPreview(meetingIndex)
  preview.isLoading = false
  preview.error = false
}

const onPreviewError = (meetingIndex) => {
  const preview = getPreview(meetingIndex)
  preview.isLoading = false
  preview.error = true
}

const getDocumentTypeLabel = (docType) => {
  const labels = {
    'CV': 'CV',
    'Diplôme': 'Diplôme',
    'Attestation EMT': 'Attestation'
  }
  return labels[docType] || docType
}
</script>

<style scoped>
/* Conteneur principal */
.details-section {
  margin-top: 40px;
}

/* Carte de meeting */
.user-file {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  background-color: #ffffff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  page-break-inside: avoid;
}

/* En-tête avec photo et nom */
.persona-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

.persona-icon {
  width: 50px;
  height: 50px;
  margin-right: 15px;
  border-radius: 50%;
  object-fit: cover;
}

.persona-title {
  font-size: 22px;
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.persona-subtitle {
  font-size: 14px;
  color: #7f8c8d;
  margin: 0;
}

/* Titre de section */
.section-title {
  font-size: 18px;
  color: #2c3e50;
  margin-top: 20px;
  margin-bottom: 10px;
  font-weight: 500;
  border-left: 4px solid #3498db;
  padding-left: 10px;
}

/* Contenu de section - Grid 2 colonnes */
.section-content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 5%;
}

.section-content p {
  margin: 0;
  font-size: 15px;
  color: #34495e;
}

.section-content strong {
  color: #2c3e50;
}

/* Section Documents - pleine largeur */
.documents-section {
  grid-column: 1 / -1;
  position: relative;
}

.documents-grid {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.document-item {
  margin-bottom: 10px;
}

.document-icon-wrapper {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.document-icon-wrapper:hover {
  transform: translateY(-2px);
}

.document-link {
  text-decoration: none;
  color: inherit;
}

.document-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  min-width: 100px;
}

.document-icon-wrapper:hover .document-icon {
  background-color: #e3f2fd;
  border-color: #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.document-icon-file {
  width: 48px;
  height: 48px;
  background-color: #2196f3;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z'/%3E%3C/svg%3E") no-repeat center;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z'/%3E%3C/svg%3E") no-repeat center;
  margin-bottom: 10px;
}

.document-type {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
}

/* Aperçu de document */
.document-preview-fullwidth {
  width: 100%;
  margin-top: 20px;
  border: 2px solid #e3f2fd;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.4s ease;
}

.document-preview-fullwidth.show {
  max-height: 600px;
  opacity: 1;
  transform: translateY(0);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

.preview-title {
  font-weight: 600;
  font-size: 16px;
}

.preview-close {
  border: none;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.preview-content {
  height: 400px;
  display: flex;
  flex-direction: column;
  position: relative;
}

.preview-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 2;
}

.loading-spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.preview-error,
.unsupported-preview {
  padding: 40px;
  text-align: center;
  color: #666;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-icon {
  width: 80px;
  height: 80px;
  background-color: #ccc;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z'/%3E%3C/svg%3E") no-repeat center;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z'/%3E%3C/svg%3E") no-repeat center;
  margin-bottom: 20px;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-actions {
  padding: 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  text-align: center;
}

.download-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.download-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.download-icon {
  width: 20px;
  height: 20px;
  background-color: white;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z'/%3E%3C/svg%3E") no-repeat center;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z'/%3E%3C/svg%3E") no-repeat center;
}

/* Section formation hours - hérite du grid */
.formation-hours {
  display: contents;
}

/* Footer du meeting */
.meeting-footer {
  margin-top: 20px;
}

.meeting-footer p {
  margin: 0;
}

/* Impression */
@media print {
  .meeting-details {
    page-break-before: always;
  }

  .meeting-details.page-break {
    page-break-before: always;
  }

  .document-preview-fullwidth,
  .Documents {
    display: none !important;
  }
}
</style>
